import { UploadSimple } from '@phosphor-icons/react';
import type { WorkspaceDto } from '@reelplicate/api-client/model';
import {
  getListWorkspacesQueryKey,
  useCreateLogoUpload,
  useRemoveLogo,
  useSetLogo,
} from '@reelplicate/api-client/workspaces';
import { Avatar, Button, Inline, Stack, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { IMAGE_TYPES, imageProblem, type ImageType, putToSignedUrl } from '@/lib/upload';

/**
 * The "Workspace logo" row of Settings → General: the logo (or initials),
 * Upload and Remove. Uploading asks the API for a presigned URL, PUTs the
 * file straight to storage, then sets it as the logo.
 */
export function WorkspaceLogo({
  workspace,
  name,
}: {
  workspace: WorkspaceDto;
  name: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();
  const createUpload = useCreateLogoUpload();
  const setLogo = useSetLogo();
  const removeLogo = useRemoveLogo();

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListWorkspacesQueryKey() });

  const upload = async (file: File) => {
    const problem = await imageProblem(file, { what: 'logo', minPx: 256 });
    if (problem) {
      toast.error(problem);
      return;
    }
    setUploading(true);
    try {
      const { url, key } = await createUpload.mutateAsync({
        id: workspace.id,
        data: { contentType: file.type as ImageType, size: file.size },
      });
      await putToSignedUrl(url, file);
      await setLogo.mutateAsync({ id: workspace.id, data: { key } });
      await refresh();
      toast.success('Logo updated');
    } catch (error) {
      // API failures already raised a toast; the storage PUT didn't.
      if (error instanceof Error && error.message.startsWith('Upload failed')) {
        toast.error('The upload didn’t go through. Try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try {
      await removeLogo.mutateAsync({ id: workspace.id });
    } catch {
      return;
    }
    await refresh();
  };

  return (
    <Inline gap={4} wrap className="py-5">
      <Avatar size={56} name={name} src={workspace.logoUrl ?? undefined} alt="" />
      <Stack gap={0.5} className="min-w-0 flex-1">
        <p className="text-body-md-strong text-text-primary">Workspace logo</p>
        <p className="text-body-sm text-text-secondary">
          {workspace.uploadsEnabled
            ? 'Square PNG or SVG, at least 256 px. Shown in the sidebar and on invites.'
            : 'Uploads aren’t set up yet.'}
        </p>
      </Stack>
      {workspace.logoUrl && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void remove()}
          loading={removeLogo.isPending}
        >
          Remove
        </Button>
      )}
      <input
        ref={input}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        className="sr-only"
        tabIndex={-1}
        aria-label="Logo file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void upload(file);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        leadingIcon={UploadSimple}
        disabled={!workspace.uploadsEnabled}
        loading={uploading}
        onClick={() => input.current?.click()}
      >
        Upload logo
      </Button>
    </Inline>
  );
}
